import { SubscriptionStatus, type Subscription } from "@prisma/client";

import { type PaddleSubscription } from "@musicfy/types/paddle-server";

import {
  AdditionalVoicesId,
  PRODUCT_ID_TO_PLAN_MAP,
  ProductId,
  type TAdditionalVoicesId,
} from "../contants/Subscriptions";

const extractPaddleSubscriptionData = (
  data: PaddleSubscription
): Subscription | null => {
  const subscriptionCheckoutItem = data.items.find((item) =>
    Object.values(ProductId).includes(item.price.product_id)
  );
  const customData = data.custom_data;

  if (!subscriptionCheckoutItem || !customData) {
    return null;
  }

  const additionalVoicesCheckoutItem = data.items.find((item) =>
    Object.values(AdditionalVoicesId).includes(
      item.price.id as TAdditionalVoicesId
    )
  );

  const subscriptionPrice = subscriptionCheckoutItem.price;
  const additionalVoicesPrice = additionalVoicesCheckoutItem?.price;

  const interval = data.billing_cycle.interval;

  const plan =
    PRODUCT_ID_TO_PLAN_MAP[subscriptionCheckoutItem.price.product_id];

  const userId = customData.userId;
  const customerId = data.customer_id;
  const subscriptionId = data.id;

  let startsAt = data.current_billing_period?.starts_at;
  let endsAt = data.current_billing_period?.ends_at;
  let status = data.status;

  if (data.scheduled_change?.action === "pause") {
    startsAt = data.scheduled_change.effective_at;
    endsAt = data.scheduled_change.resume_at || undefined;
    status = SubscriptionStatus.paused;
  }

  let canceledAt: Date | null = null;

  if (data.scheduled_change?.action === "cancel") {
    canceledAt = new Date(data.scheduled_change.effective_at);
  } else if (data.canceled_at) {
    canceledAt = new Date(data.canceled_at);
  }

  const additionalVoiceSlots = additionalVoicesCheckoutItem?.quantity ?? 0;

  const totalPrice =
    Number(subscriptionPrice.unit_price.amount) +
    Number(additionalVoicesPrice?.unit_price.amount ?? 0) *
      additionalVoiceSlots;

  const subscriptionData: Subscription = {
    plan: plan,
    interval: interval,
    status: status,
    // Always the catalog unit price in USD minor units (cents), never the
    // customer's charged local currency — our Paddle Price objects are all
    // defined in USD, so `/ 100` on this field elsewhere (SubscriptionProvider)
    // is safe without parsePaddleAmount. If a non-USD Price object is ever
    // added, that assumption breaks for zero-decimal currencies (CLP/JPY/KRW/VND).
    price: totalPrice,
    currency: subscriptionPrice.unit_price.currency_code,
    periodEnd: endsAt ? new Date(endsAt) : null,
    periodStart: startsAt ? new Date(startsAt) : null,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    canceledAt: canceledAt,
    pendingPlan: null,
    pendingInterval: null,
    pendingChangeAt: null,
    stripeScheduleId: null,
    customerId: customerId,
    subscriptionId: subscriptionId,
    userId: userId,
    additionalVoiceSlots: additionalVoiceSlots,
    freeVoiceSlots: 0,
    provider: "paddle",
  };

  return subscriptionData;
};

export { extractPaddleSubscriptionData };
